import {
  CaptionButton,
  FullscreenButton,
  isTrackCaptionKind,
  MuteButton,
  PIPButton,
  PlayButton,
  Tooltip,
  useMediaState,
} from '@vidstack/react'
import {
  ClosedCaptionsIcon,
  ClosedCaptionsOnIcon,
  FullscreenExitIcon,
  FullscreenIcon,
  MuteIcon,
  PauseIcon,
  PictureInPictureExitIcon,
  PictureInPictureIcon,
  PlayIcon,
  VolumeHighIcon,
  VolumeLowIcon,
} from '@vidstack/react/icons'

import {IconPlay} from '@sdox/icons'

import {Button} from '../../Button'

import type {ComponentProps} from 'react'
import type {PlayButtonProps, TooltipPlacement} from '@vidstack/react'

export interface MediaButtonProps {
  tooltipPlacement: TooltipPlacement
}

export const OverlayButtons = ({loading, ...restProps}: ComponentProps<'div'> & {loading?: boolean}) => (
  <div {...restProps}>
    <Button
      leading={<IconPlay />}
      loading={loading}
      className={'!flex @block-large:!hidden'}
      size={'medium'}
    />
    <Button
      leading={<IconPlay />}
      loading={loading}
      className={'!hidden @block-large:!flex'}
      size={'large'}
    />
  </div>
)

export const buttonClass = `text-white group ring-brand-primary relative
    inline-flex h-10 w-10 cursor-pointer items-center justify-center
        rounded-md outline-none ring-inset hover:bg-white/20 data-[focus]:ring-4`

export const tooltipClass = `bg-white animate-out fade-out slide-out-to-bottom-2 data-[visible]:animate-in
    data-[visible]:fade-in data-[visible]:slide-in-from-bottom-4 z-10 rounded-sm bg-black/90
    px-2 py-0.5 text-sm font-medium text-black parent-data-[open]:hidden`

export const Play = ({tooltipPlacement, ...restProps}: MediaButtonProps & PlayButtonProps) => {
  const isPaused = useMediaState('paused')

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <PlayButton
          className={buttonClass}
          aria-label={isPaused ? 'Play' : 'Pause'}
          {...restProps}
        >
          {isPaused ? (
            <PlayIcon className={'size-8'} />
          ) : (
            <PauseIcon className={'size-8'} />
          )}
        </PlayButton>
      </Tooltip.Trigger>
      <Tooltip.Content
        className={tooltipClass}
        placement={tooltipPlacement}
      >
        {isPaused ? 'Play' : 'Pause'}
      </Tooltip.Content>
    </Tooltip.Root>
  )
}

export const Mute = ({tooltipPlacement}: MediaButtonProps) => {
  const volume = useMediaState('volume')
  const isMuted = useMediaState('muted')

  let icon = <VolumeHighIcon className={'size-8'} />
  if (isMuted || volume === 0) icon = <MuteIcon className={'size-8'} />
  if (volume < 0.5) icon = <VolumeLowIcon className={'size-8'} />

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <MuteButton className={buttonClass}>
          {icon}
        </MuteButton>
      </Tooltip.Trigger>
      <Tooltip.Content
        className={tooltipClass}
        placement={tooltipPlacement}
      >
        {isMuted ? 'Unmute' : 'Mute'}
      </Tooltip.Content>
    </Tooltip.Root>
  )
}

export const Caption = ({tooltipPlacement}: MediaButtonProps) => {
  const track = useMediaState('textTrack')
  const isOn = track && isTrackCaptionKind(track)

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <CaptionButton className={buttonClass}>
          {isOn ? (
            <ClosedCaptionsOnIcon className={'size-8'} />
          ) : (
            <ClosedCaptionsIcon className={'size-8'} />
          )}
        </CaptionButton>
      </Tooltip.Trigger>
      <Tooltip.Content
        className={tooltipClass}
        placement={tooltipPlacement}
      >
        {isOn ? 'Closed-Captions Off' : 'Closed-Captions On'}
      </Tooltip.Content>
    </Tooltip.Root>
  )
}

export const PIP = ({tooltipPlacement}: MediaButtonProps) => {
  const isActive = useMediaState('pictureInPicture')

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <PIPButton className={buttonClass}>
          {isActive ? (
            <PictureInPictureExitIcon className={'size-8'} />
          ) : (
            <PictureInPictureIcon className={'size-8'} />
          )}
        </PIPButton>
      </Tooltip.Trigger>
      <Tooltip.Content
        className={tooltipClass}
        placement={tooltipPlacement}
      >
        {isActive ? 'Exit PIP' : 'Enter PIP'}
      </Tooltip.Content>
    </Tooltip.Root>
  )
}

export const Fullscreen = ({tooltipPlacement}: MediaButtonProps) => {
  const isActive = useMediaState('fullscreen')

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <FullscreenButton className={buttonClass}>
          {isActive ? (
            <FullscreenExitIcon className={'size-8'} />
          ) : (
            <FullscreenIcon className={'size-8'} />
          )}
        </FullscreenButton>
      </Tooltip.Trigger>
      <Tooltip.Content
        className={tooltipClass}
        placement={tooltipPlacement}
      >
        {isActive ? 'Exit Fullscreen' : 'Enter Fullscreen'}
      </Tooltip.Content>
    </Tooltip.Root>
  )
}
